import { getFocalPointStyles } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getRealTimeInvoiceData from "@stoneage/App/Handlers/RealTimeInvoice/RealTimeInvoiceHandler";
import { realTimeCommercialInvoicePdf, realTimeFtaInvoicePdf } from "@stoneage/App/Services/RealTimeInvoice/RealTimeInvoiceService";
import { color, parseBoolean } from "@stoneage/XDL/Utilities";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    getRealTimeInvoiceData,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps>;

export interface InvoiceDetailsButtonSetStyles {
    printButtonWrapper?: InjectableCss;
    printButton?: LinkPresentationProps;
    fileButton?: LinkPresentationProps;
}

export const invoiceDetailsButtonSetStyles: InvoiceDetailsButtonSetStyles = {
    printButton: {
        color: "common.black",
        typographyProps: {
            size: "15px",
            lineHeight: "18px",
            color: "common.black",
        },
        icon: {
            iconProps: {
                src: "IconPrint",
                size: 15,
                css: css`
                    margin-right: 8px;
                `,
            },
        },
        css: css`
            margin-right: 32px;
            > span {
                display: flex;
                align-items: center;
            }
        `,
    },
    fileButton: {
        color: "common.black",
        typographyProps: {
            size: "15px",
            lineHeight: "18px",
            color: "common.black",
        },
        icon: {
            iconProps: {
                src: "IconFile",
                size: 15,
                css: css`
                    margin-right: 8px;
                `,
            },
        },
    },
    printButtonWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            row-gap: 8px;
            align-items: flex-start;
            border-bottom: 1px solid ${color("common.ui5")};
            margin-top: 12px;
            margin-bottom: 20px;
            padding-bottom: 20px;
            position: relative;
        `,
    },
};

const styles = invoiceDetailsButtonSetStyles;

const InvoiceDetailsButtonSet: React.FC<Props> = ({ getRealTimeInvoiceData }) => {
    const { value: invoice } = React.useContext(InvoiceStateContext);
    const toasterContext = React.useContext(ToasterContext);

    const [showLoading, setShowLoading] = React.useState(false);
    if (!invoice) {
        return null;
    }
    const onClickRealTimeInvoice = () => {
        setShowLoading(true);
        getRealTimeInvoiceData({
            invoiceNumber: invoice.invoiceNumber,
            isInternational: false,
            onSuccess: result => {
                toasterContext.addToast({
                    body: "File Downloaded Successfully",
                    messageType: "success",
                });
                setShowLoading(false);
            },
            onError: error => {
                setShowLoading(false);
                toasterContext.addToast({
                    body: error.errorMessage,
                    messageType: "danger",
                });
            },
        });
    };
    return (
        <StyledWrapper {...styles.printButtonWrapper}>
            <Link
                {...styles.printButton}
                onClick={onClickRealTimeInvoice}
                data-test-selector="invoiceDetails_printButton"
                target="_blank"
            >
                {translate("Print")}
            </Link>
            {parseBoolean(invoice?.properties?.isInternational) && (
                <Link
                    {...styles.printButton}
                    href={`${realTimeCommercialInvoicePdf}/${invoice.invoiceNumber}`}
                    data-test-selector="invoiceDetails_printButton"
                    target="_blank"
                >
                    {translate("Commercial Invoice")}
                </Link>
            )}
            {parseBoolean(invoice?.properties?.hasFreeTradeAgreementDocument) && (
                <Link
                    {...styles.printButton}
                    href={`${realTimeFtaInvoicePdf}/${invoice.invoiceNumber}`}
                    data-test-selector="invoiceDetails_printButton_freeTradeAgreement"
                    target="_blank"
                >
                    {translate("Free Trade Agreement")}
                </Link>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(InvoiceDetailsButtonSet),
    definition: {
        allowedContexts: ["InvoiceDetailsPage"],
        displayName: "Invoice Details Button Set",
        group: "Invoice Details",
    },
};

export default widgetModule;
